import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check, ChevronRight, Plus, Minus, MessageCircle,
  Scale, Cpu, Landmark, PenTool, ShieldPlus, Megaphone, Briefcase, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { submitLead } from "@/lib/api";
import { trackLeadConversion } from "@/lib/analytics";
import {
  YEARS, monthsForYear, getMonthData, getMonthYear, getSampleRows,
  ALL_STATES_UTS, waLink,
} from "@/lib/gstMonths";

const USE_CASES = [
  { icon: Scale, t: "CA & Tax Firms", d: "New GST registered business leads for filing, compliance and bookkeeping retainers." },
  { icon: Landmark, t: "Banks & NBFCs", d: "Current accounts, working capital and MSME credit for freshly registered businesses." },
  { icon: Cpu, t: "Accounting & ERP Software", d: "Businesses that just got a GSTIN need billing and compliance software immediately." },
  { icon: ShieldPlus, t: "Insurance", d: "Commercial covers for verified, active businesses." },
  { icon: PenTool, t: "Trademark & IP", d: "New GST registrations often need brand protection in their first months." },
  { icon: Briefcase, t: "Business Consultants", d: "Startup advisory for new business leads Mumbai to pan-India." },
  { icon: Megaphone, t: "Marketing Agencies", d: "GST company email database for precisely segmented B2B campaigns." },
];

const FAQS = [
  { q: "Where can I buy GST registered company data in India?", a: "Datorra India sells verified GST registration data — newly registered businesses with GSTIN, trade name, owner, mobile and email. Order on WhatsApp at +91 85379 42196 and receive Excel/CSV within 24 hours." },
  { q: "How much does GST registration data cost?", a: "Trial Pack ₹3,000 one-time (up to 500 records), Monthly Plan ₹30,000 per month (daily delivery, unlimited records). Annual plans are priced on request. All prices exclusive of GST." },
  { q: "Can I download the new GST registered list in Excel?", a: "Yes. Every delivery ships as a GST registered company list Excel download with a matching CSV — ready for spreadsheet review or CRM import." },
  { q: "What fields are included?", a: "GSTIN, trade name, legal name, owner/proprietor name, mobile, email, state, district, business type/constitution, registration date and taxpayer type where available." },
  { q: "How often is it updated?", a: "Our GST registration data is refreshed every 24 hours. The current month carries an 'Updating daily' badge; past months are marked complete." },
  { q: "Can I buy data for a specific month, for example September 2026?", a: "Yes. Every month has its own page and file. Pick the month from the grid above, check the sample on WhatsApp and place the order." },
  { q: "Is it legal to buy GST registration data?", a: "Yes. GST registration details are publicly available. Use them for legitimate B2B outreach, honour DND registrations and keep opt-outs current." },
  { q: "What is the difference between GST registration data and GST sales/purchase data?", a: "Registration data tells you who the business is (identity and contact). Sales/purchase data tells you what the business transacts (invoices and turnover). They are separate products — see our GST Sales & Purchase Data page." },
  { q: "What is the minimum order?", a: "The Trial Pack at ₹3,000 one-time. Custom one-time bulk files are quoted on request based on filters and volume." },
  { q: "Can I combine state and month filters?", a: "Yes. State, district, business type, taxpayer type and month filters can be combined — the dataset is built to your exact specification." },
];

const STEPS = [
  { n: "01", t: "Tell us your target", d: "State, business type, month — a short WhatsApp message is enough." },
  { n: "02", t: "Check the sample", d: "We send a sample slice on WhatsApp so you can verify fields and quality before paying." },
  { n: "03", t: "Pay and receive", d: "Confirm the order and get the full Excel/CSV within 24 hours." },
];

const CHECKLIST = [
  { c: "Freshness of data", w: "Stale lists waste calls", d: "Refreshed every 24 hours" },
  { c: "GSTIN and registration date present", w: "Proves the record is a real registration", d: "Included on every row" },
  { c: "Mobile and email coverage", w: "Determines how reachable leads are", d: "Verified owner-level contacts" },
  { c: "State / district / type filters", w: "Lets you match the file to your ICP", d: "Full filter set included" },
  { c: "Sample before payment", w: "Validates quality before commitment", d: "Sample sent on WhatsApp" },
  { c: "Delivery time and format", w: "Speed matters for time-sensitive campaigns", d: "Excel / CSV within 24 hours" },
  { c: "Support after purchase", w: "Issues surface during campaigns", d: "WhatsApp support, 7 days" },
];

export default function GstDataHub() {
  const [year, setYear] = useState(YEARS[YEARS.length - 1]);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", company: "", month: "", purpose: "Sales" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useSeo({
    title: "Buy GST Registered Company Data India | Month-wise New GST List Excel | Datorra",
    description:
      "Buy GST registered business data online. Month-wise new GST registration lists with GSTIN, trade name, owner, mobile and email. Excel/CSV, from Rs 30,000/month. Datorra India.",
    path: "/gst-data",
    jsonLd: [
      {
        id: "gsthub-breadcrumb",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://datorra.in/" },
            { "@type": "ListItem", position: 2, name: "GST Data", item: "https://datorra.in/gst-data" },
          ],
        },
      },
      {
        id: "gsthub-product",
        data: {
          "@context": "https://schema.org",
          "@type": "Product",
          name: "GST Registered Company Data — Month-wise New GST Registration Lists",
          description: "Month-wise newly registered GST business lists with GSTIN, trade name, owner, mobile and email. Excel/CSV delivery within 24 hours.",
          brand: { "@type": "Brand", name: "Datorra India" },
          areaServed: "IN",
          offers: [
            { "@type": "Offer", name: "Trial Pack", price: "3000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/gst-data" },
            { "@type": "Offer", name: "Monthly Plan", price: "30000", priceCurrency: "INR", availability: "https://schema.org/InStock", url: "https://datorra.in/gst-data" },
          ],
        },
      },
      {
        id: "gsthub-faq",
        data: {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        },
      },
    ],
  });

  const months = monthsForYear(year);
  const hubWa = waLink("Hi Datorra, I want to buy GST registered company data");

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({
        name: form.name,
        email: "not-provided@gst-enquiry.datorra.in",
        phone: form.phone,
        company: form.company,
        requirement: `GST Data — ${form.month || "Any month"} — ${form.purpose}`,
        message: `Hub enquiry. Month/State: ${form.month || "-"} · Purpose: ${form.purpose}`,
        source: "gst-data-hub",
      });
      trackLeadConversion();
      setDone(true);
      toast.success("Enquiry received. We'll reply on WhatsApp shortly.");
    } catch {
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="gst-data-hub" className="bg-white">
      {/* 1. HERO */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-20 border-b border-slate-100 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-blue-50/60 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-slate-500 flex items-center gap-1.5">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300" strokeWidth={1.75} />
            <span className="text-slate-900 font-medium">GST Data</span>
          </nav>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-5xl">
            GST Registered Company Data India: Buy <span className="font-serif-editorial italic">month-wise</span> new GST registration lists
          </h1>

          <p className="mt-6 max-w-3xl text-lg md:text-xl text-slate-600 leading-relaxed">
            Verified month-wise lists of businesses newly registered under GST — GSTIN, trade name, owner, mobile and email
            included. Delivered in Excel/CSV within 24 hours.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a href={hubWa} target="_blank" rel="noopener noreferrer" data-testid="hub-hero-sample" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 active:scale-95 transition-all">
              Get Sample Data <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </a>
            <a href="#pricing" data-testid="hub-hero-pricing" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 border border-slate-200 pl-6 pr-5 py-4 text-sm font-medium hover:border-slate-900 active:scale-95 transition-all">
              View Pricing <ChevronRight className="w-4 h-4 rotate-90" strokeWidth={1.75} />
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600" data-testid="hub-trust-strip">
            {["Daily updated", "GSTIN included", "Excel / CSV", "Pan India"].map((b) => (
              <span key={b} className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-600" strokeWidth={2.25} />
                <span className="font-medium text-slate-800">{b}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 2. MONTH-WISE GRID */}
      <section className="py-20 md:py-24 bg-white" data-testid="hub-month-grid">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ By Month</div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
                GST Registration Data by Month: {year}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {YEARS.map((y) => (
                <button
                  key={y}
                  onClick={() => setYear(y)}
                  data-testid={`year-${y}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all ${year === y ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 border border-slate-200 hover:border-slate-900"}`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {months.map((slug) => {
              const { monthName, year: y } = getMonthYear(slug);
              const data = getMonthData(slug);
              const inProgress = data?.status === "in-progress";
              return (
                <div key={slug} data-testid={`month-card-${slug}`} className="relative bg-[#fafafa] border border-slate-100 rounded-3xl p-7 flex flex-col hover:bg-white hover:shadow-md hover:border-blue-100 transition-all">
                  {inProgress && (
                    <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                      Updating daily
                    </span>
                  )}
                  <div className="font-display text-2xl font-medium text-slate-900">{monthName} {y}</div>
                  {typeof data?.total_new_registrations === "number" && (
                    <div className="mt-2 text-sm text-slate-600">
                      <span className="font-display text-lg text-slate-900 font-medium">{data.total_new_registrations.toLocaleString("en-IN")}</span> new GST registrations
                    </div>
                  )}
                  {data?.highlight_note && <p className="mt-2 text-sm text-slate-500">{data.highlight_note}</p>}
                  <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500">
                    <Check className="w-3.5 h-3.5 text-blue-600" strokeWidth={2.25} /> Excel / CSV available
                  </div>
                  <Link to={`/gst-data/${slug}`} data-testid={`month-link-${slug}`} className="mt-6 group inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white pl-5 pr-4 py-3 text-sm font-medium self-start hover:bg-blue-600 transition-colors">
                    View {monthName} {y} GST Data
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. BUY block */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Buy</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Buy GST registered company data online
          </h2>
          <div className="mt-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              When you <strong className="text-slate-900">buy GST company data</strong> from Datorra, each record carries the
              GSTIN, trade name, legal name, owner or proprietor name, mobile, email, state, district, business
              type/constitution, registration date and taxpayer type where available. It's the most complete way to{" "}
              <strong className="text-slate-900">buy GST database India</strong> coverage for newly registered businesses.
            </p>
            <p>
              Whether you want to <strong className="text-slate-900">buy newly registered GST businesses list</strong> files for
              one month, a <strong className="text-slate-900">buy GST registered company list</strong> cut by state, or{" "}
              <strong className="text-slate-900">purchase GST registration data</strong> across multiple districts, every delivery
              ships in Excel or CSV within 24 hours. The simplest way to{" "}
              <strong className="text-slate-900">buy GST data online</strong> is a single WhatsApp message — tell us the month and
              filters, approve the sample, and the file lands in your inbox.
            </p>
          </div>
        </div>
      </section>

      {/* A. 3-step */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Process</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            How to buy GST registration data in 3 steps
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-[#fafafa] border border-slate-100 rounded-3xl p-7">
                <div className="chapter-numeral text-5xl">{s.n}</div>
                <h3 className="mt-4 font-display text-xl font-medium text-slate-900">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <a href={hubWa} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-all">
              Start on WhatsApp <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* B. Buyer checklist */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Checklist</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            What to check before you buy GST data
          </h2>
          <div className="mt-12 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  <th className="p-5 font-medium">What to check</th>
                  <th className="p-5 font-medium">Why it matters</th>
                  <th className="p-5 font-medium bg-blue-50 border-l border-blue-100 text-slate-900">What Datorra provides</th>
                </tr>
              </thead>
              <tbody>
                {CHECKLIST.map((r, i) => (
                  <tr key={r.c} className={`border-t border-slate-100 ${i % 2 === 1 ? "bg-slate-50/40" : "bg-white"}`}>
                    <td className="p-5 font-medium text-slate-800">{r.c}</td>
                    <td className="p-5 text-slate-600">{r.w}</td>
                    <td className="p-5 bg-blue-50/60 border-l border-blue-100">
                      <span className="inline-flex items-center gap-2 text-slate-900 font-medium">
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" strokeWidth={2.25} />
                        {r.d}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4. Pricing */}
      <section id="pricing" className="py-20 md:py-24 bg-white scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Pricing</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            GST data <span className="font-serif-editorial italic">price</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { t: "Trial Pack", price: "₹3,000", period: "one-time", d: "1-day GST data sample, up to 500 records. Verify quality before committing.", cta: "Order Trial", featured: false },
              { t: "Monthly Plan", price: "₹30,000", period: "per month", d: "Daily delivery, all states, all business types, unlimited records.", cta: "Order Now", featured: true },
              { t: "Annual Plan", price: "Contact for best price", period: "12 months", d: "Year-long daily delivery, priority support, custom filters, API access.", cta: "Get Quote", featured: false },
            ].map((p) => (
              <div key={p.t} className={`relative rounded-3xl p-8 flex flex-col ${p.featured ? "bg-slate-900 text-white border border-slate-900 shadow-2xl shadow-slate-900/20" : "bg-white border border-slate-100"}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-8 rounded-full bg-blue-600 text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold">Most Popular</span>
                )}
                <h3 className={`font-display text-2xl font-medium ${p.featured ? "text-white" : "text-slate-900"}`}>{p.t}</h3>
                <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                  <span className={`font-display text-3xl font-light ${p.featured ? "text-white" : "text-slate-900"}`}>{p.price}</span>
                  <span className={`text-sm ${p.featured ? "text-slate-300" : "text-slate-500"}`}>{p.period}</span>
                </div>
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-slate-300" : "text-slate-600"}`}>{p.d}</p>
                <a href={hubWa} target="_blank" rel="noopener noreferrer" className={`mt-8 group inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-3 text-sm font-medium self-start transition-all ${p.featured ? "bg-white text-slate-900 hover:bg-blue-600 hover:text-white" : "bg-slate-900 text-white hover:bg-blue-600"}`}>
                  {p.cta} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </a>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-slate-500 text-sm">All prices exclusive of GST.</p>
          <p className="mt-6 max-w-3xl mx-auto text-center text-slate-600 leading-relaxed">
            On <strong className="text-slate-900">GST company data per record price</strong>: the Monthly Plan is unlimited, so
            your effective cost per record falls as you use more of the file. For one-time bulk files, we quote custom per-record
            pricing based on filters and volume — compare any <strong className="text-slate-900">GST database price India</strong>{" "}
            offer against a Datorra quote, and check what a transparent{" "}
            <strong className="text-slate-900">new GST registration list price</strong> looks like. The{" "}
            <strong className="text-slate-900">GST data subscription</strong> (₹30,000/month) is the most cost-effective option for
            teams working new registrations continuously.
          </p>
        </div>
      </section>

      {/* 5. Formats */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Formats</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Download GST registered company list in Excel and CSV
          </h2>
          <div className="mt-8 space-y-5 text-base md:text-lg text-slate-600 leading-relaxed">
            <p>
              Every delivery is a <strong className="text-slate-900">GST registered company list Excel download</strong> — a clean{" "}
              <strong className="text-slate-900">GSTIN list Excel</strong> file plus a{" "}
              <strong className="text-slate-900">new GST registration CSV download</strong> for CRM and dialler imports. A{" "}
              <strong className="text-slate-900">state wise GST list Excel</strong> split is included when you order multiple
              states, and a <strong className="text-slate-900">district wise GST business list</strong> is available for city-level
              targeting. A business type filter (proprietorship / partnership / Pvt Ltd / LLP) applies to every file.
            </p>
            <p>
              Filters available on every order: state, district, business type, taxpayer type and registration date range.
            </p>
          </div>
        </div>
      </section>

      {/* 6. Use cases */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Use Cases</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            New GST registered business leads for sales teams
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {USE_CASES.map((u) => (
              <div key={u.t} className="bg-[#fafafa] border border-slate-100 rounded-3xl p-6 hover:bg-white hover:shadow-md hover:border-blue-100 transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <u.icon className="text-blue-600" strokeWidth={1.5} style={{ width: 18, height: 18 }} />
                </div>
                <h3 className="mt-5 font-display text-lg font-medium text-slate-900">{u.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{u.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-slate-600 leading-relaxed">
            As a <strong className="text-slate-900">B2B database India</strong> provider, Datorra pairs these lists with a verified{" "}
            <strong className="text-slate-900">GST company email database</strong> layer — so sales teams get phone and inbox
            coverage from one file, across new business leads India wide.
          </p>
        </div>
      </section>

      {/* 7. Why Datorra */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Provider</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            GST data provider India: why Datorra
          </h2>
          <ul className="mt-10 space-y-4">
            {[
              "Refreshed every 24 hours from registration records",
              "Pan India — 28 states and 8 union territories",
              "Sample before you buy, sent on WhatsApp",
              "WhatsApp support, 7 days a week",
              "Custom filters on every order",
              "GST bulk data and API delivery on request",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-700 text-base md:text-lg">
                <Check className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" strokeWidth={2.25} />
                {b}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-slate-600 leading-relaxed">
            Choosing a <strong className="text-slate-900">GST database provider</strong> is mostly about freshness, verification
            and support — the three things Datorra optimizes for.
          </p>
        </div>
      </section>

      {/* 8. Sample table */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Sample</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Sample format
          </h2>
          <div className="mt-10 overflow-x-auto rounded-3xl border border-slate-200 bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                  {["Trade Name", "GSTIN", "State", "Business Type", "Registration Date"].map((h) => (
                    <th key={h} className="p-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {getSampleRows("october-2026").map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className={`p-4 font-medium text-slate-900 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.company}</td>
                    <td className={`p-4 font-mono text-xs text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.gstin}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.state}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.type}</td>
                    <td className={`p-4 text-slate-700 ${r.blur ? "filter blur-[4px] select-none" : ""}`}>{r.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-slate-400">Sample format. Request real sample on WhatsApp.</p>
        </div>
      </section>

      {/* D. Full coverage grid */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Coverage</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Pan India coverage — every state &amp; union territory
          </h2>
          <div className="mt-10 flex flex-wrap gap-2">
            {ALL_STATES_UTS.map((s) => (
              <a
                key={s}
                href={waLink(`Hi Datorra, I want GST registration data for ${s}`)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2.5 text-sm text-slate-800 hover:border-blue-600 hover:text-blue-700 transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 text-blue-600" strokeWidth={1.75} />
                {s}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* C. Complete guide */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Guide</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Complete guide: how to buy and use GST registration data for B2B sales
          </h2>

          <div className="mt-12 space-y-10 text-base md:text-lg text-slate-700 leading-relaxed">
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">What GST registration data contains</h3>
              <p className="mt-4">
                Every record includes the GSTIN, trade name, legal name, owner or proprietor name, mobile, email, state,
                district, business type/constitution, registration date and taxpayer type. The GSTIN is a permanent, verifiable
                identifier — easy to de-duplicate and enrich inside your CRM.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">GST vs MCA data — who should buy which</h3>
              <p className="mt-4">
                GST data covers every tax-registered business — roughly 1.5 crore entities including proprietorships,
                partnerships, trusts and HUFs. MCA data covers only incorporated companies (Pvt Ltd, LLP, OPC, Public Ltd) —
                around 30 lakh. Buy GST when you want width, MCA when you want director-level depth. Most enterprise teams run
                both in parallel.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Why newly GST-registered businesses are strong leads</h3>
              <p className="mt-4">
                A business that just received a GSTIN needs everything in its first months: accounting software, a current
                account, insurance, compliance help, marketing. Vendor decisions are still open — which is why month-wise GST
                registration data, worked while the registration is days old, converts better than any stale list.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Filtering by state, district and business type</h3>
              <p className="mt-4">
                Start with geography (state, then district), then business type, then taxpayer type. Tight filters beat big
                lists — a 5,000-row file matched to your ICP outperforms a 5-lakh-row dump every time.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Excel vs CSV — and how to import</h3>
              <p className="mt-4">
                Excel (.xlsx) for human review and team slicing; CSV for CRM and WhatsApp tool imports. Both ship with every
                Datorra order — UTF-8, de-duplicated, with a column dictionary.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Outreach tips</h3>
              <p className="mt-4">
                Reference the registration date in your opening line. Sequence channels: WhatsApp day 1, email day 3, call
                day 5, LinkedIn day 7. Owner-level mobile numbers mean no gatekeepers — one specific message beats five generic ones.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Legal and compliance basics</h3>
              <p className="mt-4">
                GST registration details are publicly available. Use them for legitimate B2B outreach, honour DND
                registrations and keep opt-outs current. A data-use agreement accompanies every Datorra delivery.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Measuring results</h3>
              <p className="mt-4">
                Tag every lead by month, state, type and turnover band in your CRM. Track contact rate, meeting rate and close
                rate per segment. After 60 days, refine your filters with real conversion data.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Common mistakes</h3>
              <p className="mt-4">
                Buying stale dumps, skipping the sample, over-filtering, running single-channel outreach, ignoring DND.
                Avoid all five and a month-wise GST file becomes one of the highest-ROI lists your team owns.
              </p>
            </div>
            <a href={hubWa} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-all">
              Get Sample Data on WhatsApp <MessageCircle className="w-4 h-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* F. Lead capture */}
      <section className="py-20 md:py-24 bg-[#fafafa] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Enquire</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
              Tell us which <span className="font-serif-editorial italic">month</span> you need
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Share your target month, state and purpose. We'll reply with a scoped quote and sample on WhatsApp.
            </p>
          </div>
          <div className="md:col-span-7 bg-white rounded-3xl border border-slate-100 p-8 md:p-10">
            {done ? (
              <div className="flex flex-col items-start" data-testid="hub-enquiry-success">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                  <Check className="w-6 h-6 text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="mt-6 font-display text-2xl text-slate-900">We've got it.</div>
                <div className="mt-2 text-slate-600">Our team will reach out shortly — or continue on WhatsApp for a faster reply.</div>
                <a href={hubWa} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-6 pr-5 py-4 text-sm font-medium hover:brightness-95 transition-all">
                  <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> Continue on WhatsApp
                </a>
              </div>
            ) : (
              <form onSubmit={submitEnquiry} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="hub-enquiry-form">
                {[
                  { k: "name", label: "Name*", required: true, type: "text" },
                  { k: "phone", label: "Mobile*", required: true, type: "tel" },
                  { k: "company", label: "Company", required: false, type: "text" },
                  { k: "month", label: "Which month / state", required: false, type: "text" },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">{f.label}</label>
                    <input
                      data-testid={`hub-enquiry-${f.k}`}
                      type={f.type}
                      required={f.required}
                      value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2.5 text-slate-900 text-base outline-none transition-colors"
                    />
                  </div>
                ))}
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Purpose</label>
                  <select
                    data-testid="hub-enquiry-purpose"
                    value={form.purpose}
                    onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-2.5 text-slate-900 text-base outline-none"
                  >
                    {["Sales", "Marketing", "Banking", "CA firm", "Other"].map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <button data-testid="hub-enquiry-submit" disabled={loading} className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-6 pr-5 py-4 text-sm font-medium hover:bg-blue-600 transition-all disabled:opacity-60">
                    {loading ? "Sending…" : "Request Sample"} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* 9E. FAQ */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ FAQ</div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-slate-900 leading-[1.05]">
            Frequently asked questions
          </h2>
          <div className="mt-12 divide-y divide-slate-200">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="py-1">
                  <button data-testid={`hub-faq-toggle-${i}`} onClick={() => setOpenFaq(open ? -1 : i)} aria-expanded={open} className="w-full py-6 flex items-start justify-between gap-6 text-left group">
                    <span className="font-display text-lg md:text-xl font-medium text-slate-900 group-hover:text-blue-600 transition-colors">{f.q}</span>
                    <span className="mt-1 flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center group-hover:border-slate-900 transition-colors">
                      {open ? <Minus className="w-4 h-4" strokeWidth={1.75} /> : <Plus className="w-4 h-4" strokeWidth={1.75} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.2, 0.7, 0.15, 1] }} className="overflow-hidden">
                        <p className="pb-6 pr-16 text-slate-600 leading-relaxed text-base md:text-lg">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 10. CTA band */}
      <section className="relative py-20 md:py-24 bg-slate-900 text-white overflow-hidden">
        <div className="absolute -top-16 -left-16 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
            Ready to reach this month's <span className="font-serif-editorial italic">newly registered</span> GST businesses?
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a href={hubWa} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white pl-7 pr-6 py-5 text-base font-medium hover:brightness-95 active:scale-95 transition-all">
              <MessageCircle className="w-5 h-5" strokeWidth={1.75} /> WhatsApp Now
            </a>
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white text-slate-900 pl-7 pr-6 py-5 text-base font-medium hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition-all">
              Request Access <ArrowUpRight className="w-5 h-5" strokeWidth={1.75} />
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap justify-center gap-2 text-xs">
            <Link to="/gst-database" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">GST Database</Link>
            <Link to="/mca-data" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">MCA Data by Month</Link>
            <Link to="/gst-sales-purchase-data" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">GST Sales & Purchase</Link>
            <Link to="/contact" className="rounded-full border border-white/20 px-4 py-2 text-white/80 hover:border-white hover:text-white transition-colors">Contact</Link>
          </div>
        </div>
      </section>

      <div className="bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 text-xs text-slate-400">
          GST registration details are publicly available. Use them for legitimate B2B outreach, honour DND registrations and keep opt-outs current.
        </div>
      </div>
    </div>
  );
}
